import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const inputVariants = cva(
  'w-full border transition-colors bg-white placeholder:text-slate-400',
  {
    variants: {
      variant: {
        default:
          'border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
        emerald:
          'border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500',
        ghost:
          'border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500',
        error:
          'border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500',
      },
      inputSize: {
        sm: 'py-2 text-sm rounded-lg',
        md: 'py-2.5 text-sm rounded-xl',
        lg: 'py-3 text-base rounded-xl',
      },
    },
    defaultVariants: {
      variant: 'default',
      inputSize: 'md',
    },
  }
);

// Compute padding based on icon presence
function getIconPadding(leftIcon?: boolean, rightIcon?: boolean): string {
  if (leftIcon && rightIcon) return 'pl-10 pr-10';
  if (leftIcon) return 'pl-10 pr-4';
  if (rightIcon) return 'pl-4 pr-10';
  return 'px-4';
}

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  error?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, inputSize, leftIcon, rightIcon, error, ...props }, ref) => {
    const iconPadding = getIconPadding(!!leftIcon, !!rightIcon);
    const effectiveVariant = error ? 'error' : variant;

    return (
      <div className="relative w-full">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
            {leftIcon}
          </div>
        )}
        <input
          ref={ref}
          className={cn(
            inputVariants({ variant: effectiveVariant, inputSize }),
            iconPadding,
            className
          )}
          {...props}
        />
        {rightIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {rightIcon}
          </div>
        )}
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

// Textarea variant
const textareaVariants = cva(
  'w-full border transition-colors bg-white placeholder:text-slate-400 resize-none',
  {
    variants: {
      variant: {
        default:
          'border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
        emerald:
          'border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500',
        ghost:
          'border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500',
        error:
          'border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500',
      },
      inputSize: {
        sm: 'p-3 text-sm rounded-lg',
        md: 'p-4 text-sm rounded-xl',
        lg: 'p-5 text-base rounded-xl',
      },
    },
    defaultVariants: {
      variant: 'default',
      inputSize: 'md',
    },
  }
);

export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'>,
    VariantProps<typeof textareaVariants> {
  error?: string;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, variant, inputSize, error, ...props }, ref) => {
    const effectiveVariant = error ? 'error' : variant;

    return (
      <div className="w-full">
        <textarea
          ref={ref}
          className={cn(textareaVariants({ variant: effectiveVariant, inputSize }), className)}
          {...props}
        />
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
      </div>
    );
  }
);
Textarea.displayName = 'Textarea';

export { Input, Textarea, inputVariants, textareaVariants };
